import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Skeleton,
  Button,
  Drawer,
  IconButton,
  Slider,
  FormControlLabel,
  Checkbox,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useCart } from "../../context/CartContext";
import VendorSponsorshipCard from "./VendorSponsorshipCard";
const VendorStore = () => {
  const { addToCart } = useCart();
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [tab, setTab] = useState("products");
  
  // Filter & Sort States
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [sortBy, setSortBy] = useState("relevance");

  useEffect(() => {
    api
      .get(`/vendor/${vendorId}/store`)
      .then((res) => {
        setVendor(res.data.vendor);
        setProducts(res.data.products);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [vendorId]);

  const refreshVendor = async () => {
    const res = await api.get(`/vendor/${vendorId}/store`);
    setVendor(res.data.vendor);
  };

  // Extract unique categories from products
  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  // Extract price range from products
  const priceStats = useMemo(() => {
    if (products.length === 0) return { min: 0, max: 100000 };
    const prices = products.map((p) => p.price);
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices)),
    };
  }, [products]);

  // Filter & Sort Logic
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter((p) => {
      // Price filter
      if (p.price < priceRange[0] || p.price > priceRange[1])
        return false;

      // Category filter
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(p.category)
      )
        return false;

      // Rating filter
      if (selectedRating > 0) {
        const productRating = p.rating?.avg || 0;
        if (productRating < selectedRating) return false;
      }

      return true;
    });

    // Sort
    const sorted = [...filtered];
    switch (sortBy) {
      case "price_low":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_high":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        sorted.sort(
          (a, b) => (b.rating?.avg || 0) - (a.rating?.avg || 0)
        );
        break;
      case "newest":
        sorted.sort(
          (a, b) =>
            new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        break;
      case "popularity":
        sorted.sort(
          (a, b) => (b.reviewCount || 0) - (a.reviewCount || 0)
        );
        break;
      default: // relevance
        break;
    }

    return sorted;
  }, [products, priceRange, selectedCategories, selectedRating, sortBy]);

  // Reset filters on tab change
  useEffect(() => {
    setPriceRange([priceStats.min, priceStats.max]);
    setSelectedCategories([]);
    setSelectedRating(0);
    setSortBy("relevance");
  }, [tab, priceStats]);

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (!vendor) return <Typography>Store not found</Typography>;

  /* ================= FILTER SIDEBAR ================= */
  const FilterSidebar = () => (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        maxWidth: 280,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Filters
        </Typography>
        <IconButton
          size="small"
          onClick={() => setFilterDrawerOpen(false)}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* PRICE FILTER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          Price
        </Typography>
        <Slider
          value={priceRange}
          onChange={(e, newValue) => setPriceRange(newValue)}
          min={priceStats.min}
          max={priceStats.max}
          step={100}
          valueLabelDisplay="auto"
          marks={[
            { value: priceStats.min, label: `₹${priceStats.min}` },
            { value: priceStats.max, label: `₹${priceStats.max}` },
          ]}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, fontSize: 12, color: "text.secondary" }}>
          <span>₹{priceRange[0]}</span>
          <span>₹{priceRange[1]}</span>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* CATEGORY FILTER */}
      {categories.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Category
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCategories.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCategories([...selectedCategories, cat]);
                      } else {
                        setSelectedCategories(
                          selectedCategories.filter((c) => c !== cat)
                        );
                      }
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{cat}</Typography>}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* RATING FILTER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Rating
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {[4, 3, 2, 1].map((rating) => (
            <FormControlLabel
              key={rating}
              control={
                <Checkbox
                  size="small"
                  checked={selectedRating === rating}
                  onChange={(e) => {
                    setSelectedRating(e.target.checked ? rating : 0);
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13 }}>
                  ⭐ {rating}+ Star{rating !== 1 ? "s" : ""}
                </Typography>
              }
            />
          ))}
        </Box>
      </Box>

      {/* CLEAR FILTERS */}
      <Button
        fullWidth
        variant="outlined"
        sx={{
          mt: 2,
          borderColor: "#ff9900",
          color: "#ff9900",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": {
            bgcolor: "#fff3e0",
            borderColor: "#ff9900",
          },
        }}
        onClick={() => {
          setPriceRange([priceStats.min, priceStats.max]);
          setSelectedCategories([]);
          setSelectedRating(0);
          setSortBy("relevance");
        }}
      >
        Clear All Filters
      </Button>
    </Paper>
  );

  const handleAddToCart = (e, product) => {
    e.stopPropagation();

    addToCart({
      id: product._id,
      title: product.title,
      price: product.price,
      imageUrl: product.images?.[0],
      qty: 1,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
    });
  };

  /* ================= PRODUCT CARD ================= */
  const ProductCard = (p) => {
    const stock = p?.stock ?? 0;
    const inStock = stock > 0;
    console.log("STOCK", p.title, stock);

    const maxTitleLength = 40;
    const truncatedTitle = p.title
      ? p.title.length > maxTitleLength
        ? p.title.slice(0, maxTitleLength).trimEnd() + "..."
        : p.title
      : "";

    return (
      <Card
        sx={{
          width: "100%",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          // minHeight: 360,
          // maxHeight: 360,
          boxSizing: "border-box",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }}
        onClick={() => navigate(`/product/${p._id}`)}
        onMouseEnter={() => setHoveredId(p._id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        {/* IMAGE */}
<Box
  sx={{
    height: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#fff",
    p: 1,
    position: "relative",
    overflow: "hidden", // ✅ prevents spill
  }}
>
  <img
    src={
      hoveredId === p._id && p.images?.[1]
        ? p.images[1]
        : p.images?.[0] || "/placeholder-product.png"
    }
    alt=""
    style={{
      maxHeight: "100%",
      maxWidth: "100%",
      objectFit: "contain",
    }}
  />

</Box>

        {/* OUT OF STOCK */}
        {!inStock && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              bgcolor: "#fdecea",
              color: "#b71c1c",
              px: 1,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Out of Stock
          </Box>
        )}

        {/* CONTENT */}
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flexGrow: 1,
            p: 0.75,
            // minHeight: 80,
            // maxHeight: 80,
            overflow: "hidden",
          }}
        >
          <Tooltip title={p.title || ""} arrow placement="top">
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.2,
                height: 34,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
                whiteSpace: "normal",
                mb: 0.25,
              }}
            >
              {truncatedTitle}
            </Typography>
          </Tooltip>
          <Typography
            fontWeight={700}
            color="#B12704"
            sx={{ mb: 0.5 }}
          >
            ₹{p.price}
          </Typography>

          {inStock && (
            <Button
              size="small"
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#ff9900",
                color: "#000",
                fontWeight: 600,
                textTransform: "none",
                p: "6px 8px",
                minHeight: 30,
                fontSize: 12,
                "&:hover": { bgcolor: "#ff8800" },
              }}
              onClick={(e) => handleAddToCart(e, p)}
            >
              Add to Cart
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ bgcolor: "#eaeded", minHeight: "100vh" }}>
      
      {/* ================= BANNER ================= */}
      <Box
        sx={{
          // mt:"1px",
          height: 220,
          backgroundImage: `url(${vendor.banner || "/default-banner.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: 3,
          mt: -6,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2 }}>
          <img
            src={vendor.logo || "/placeholder.png"}
            alt=""
            style={{ width: 80, height: 80, borderRadius: 8 }}
          />

<Box sx={{ flex: 1 }}>

  {/* 🏷 NAME + BADGES */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="h5" fontWeight={700}>
      {vendor.name}
    </Typography>

    {/* ✔️ VERIFIED BADGE */}
    {vendor.isVerified && (
      <Box
        sx={{
          bgcolor: "#e6f4ea",
          color: "#1e8e3e",
          px: 1,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        ✔ Verified
      </Box>
    )}

    {/* 📢 SPONSORED BADGE */}
<VendorSponsorshipCard
  vendorId={vendor._id}
  sponsorshipStatus={vendor.sponsorshipStatus}  
  onApplied={refreshVendor}
/>
  </Box>

  {/* 📝 TAGLINE */}
  <Typography color="text.secondary">
    {vendor.tagline || "Trusted seller"}
  </Typography>

  {/* ⭐ RATING */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
    <Typography sx={{ fontWeight: 600 }}>
      ⭐ {vendor.rating?.avg?.toFixed(1) || "0.0"}
    </Typography>

    <Typography variant="body2" color="text.secondary">
      ({vendor.rating?.totalReviews || 0} reviews)
    </Typography>
  </Box>

  {/* 📧 CONTACT */}
  <Typography variant="body2" sx={{ mt: 0.5 }}>
    Contact: {vendor.email}
  </Typography>
</Box>

{/* ❤️ FOLLOW BUTTON */}
<Button
  variant="contained"
  sx={{
    bgcolor: "#ff9900",
    color: "#000",
    fontWeight: 600,
    "&:hover": { bgcolor: "#e68a00" },
  }}
>
  Follow
</Button>
        </Box>
      </Paper>

      {/* ================= TABS ================= */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          mt: 2,
          display: "flex",
          gap: 3,
          px: 2,
          borderBottom: "1px solid #ddd",
        }}
      >
        {["home", "products", "about"].map((t) => (
          <Typography
            key={t}
            onClick={() => setTab(t)}
            sx={{
              cursor: "pointer",
              pb: 1,
              borderBottom:
                tab === t ? "2px solid #000" : "none",
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t.toUpperCase()}
          </Typography>
        ))}
      </Box>

      {/* ================= HOME ================= */}
      {tab === "home" && (
        <Box sx={{ maxWidth: 1200, mx: "auto", mt: 3, px: 2, pb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Featured Products
          </Typography>

          <Grid container spacing={2}>
            {products.slice(0, 4).map((p) => (
              <Grid item xs={12} sm={6} md={3} key={p._id}>
                {ProductCard(p)}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ================= PRODUCTS (WITH FILTERS) ================= */}
      {tab === "products" && (
        <Box sx={{ maxWidth: 1400, mx: "auto", mt: 3, px: 2, pb: 3 }}>
          {/* TOOLBAR: Filter Button + Sort Dropdown */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  borderRadius: 1,
                  bgcolor: "#f5f5f5",
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {filteredAndSortedProducts.length} products
              </Typography>
            </Box>

            {/* SORT DROPDOWN */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                Sort by:
              </Typography>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 200 },
                  bgcolor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="relevance">Relevance</MenuItem>
                <MenuItem value="price_low">Price: Low to High</MenuItem>
                <MenuItem value="price_high">Price: High to Low</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="popularity">Most Popular</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* MAIN CONTENT: Sidebar + Products */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                flex: "0 0 320px",
                position: "sticky",
                top: 110,
                alignSelf: "flex-start",
              }}
            >
              <FilterSidebar />
            </Box>

            <Box sx={{ flex: 1 }}>
              {filteredAndSortedProducts.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No products match your filters
                  </Typography>
                  <Button
                    sx={{ mt: 2, color: "#ff9900" }}
                    onClick={() => {
                      setPriceRange([priceStats.min, priceStats.max]);
                      setSelectedCategories([]);
                      setSelectedRating(0);
                      setSortBy("relevance");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredAndSortedProducts.map((p) => (
                    <Grid item xs={12} sm={6} md={4} key={p._id}>
                      {ProductCard(p)}
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* FILTER DRAWER (Mobile) */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <FilterSidebar />
        </Box>
      </Drawer>

      {/* ================= ABOUT ================= */}
      {tab === "about" && (
  <Box sx={{ maxWidth: 900, mx: "auto", mt: 3 }}>

    {/* 🏢 ABOUT */}
    <Typography variant="h6" gutterBottom>
      About {vendor.businessName || vendor.name}
    </Typography>

    <Typography color="text.secondary" sx={{ mb: 3 }}>
      {vendor.description || "No description available"}
    </Typography>

    {/* 🖼 STORE GALLERY */}
    {vendor.storefrontImages?.length > 0 && (
      <>
        <Typography variant="h6" gutterBottom>
          Store Gallery
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {vendor.storefrontImages.map((img, i) => (
            <Grid item xs={6} md={4} key={i}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  height: 160,
                  cursor: "pointer",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "scale(1.03)",
                  },
                }}
              >
                <img
                  src={img}
                  alt="store"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    )}

    {/* 📍 ADDRESS */}
    {vendor.address && (
      <>
        <Typography variant="h6" gutterBottom>
          Address
        </Typography>

        <Typography color="text.secondary">
          {vendor.address.addressLine1}, {vendor.address.city},{" "}
          {vendor.address.state} - {vendor.address.pincode}
        </Typography>
      </>
    )}

  </Box>
)}
    </Box>
  );
};

export default VendorStore;